import React, { useContext } from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../routes/routes";
import { getSubscriptionPlan } from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import { encrypt, formatDateCommon } from "../../utils/common";
import "./UpgradePlanPage.scss";

interface SubscriptionData {
  PlanId: number;
  PlanName: string;
  IsSubscriptionActive: boolean;
  UserResponseCount: number;
  AllowWebHookNotification: boolean;
  AllowSentiment: boolean;
  AllowPublicSurvey: boolean;
  AllowDemography: boolean;
  Report: boolean;
  PlanEndDate: string;
  PlanStartDate: string;
  plan_id?: number;
  plan_end_date?: string;
  TotalTopUpUnit: number;
  TotalConsumedUnit: number;
  UnitBalance: number;
}

interface SubscriptionPlanProps {
  visible: boolean;
  onClose: () => void;
  data: SubscriptionData;
}

const SubscriptionPlan: React.FC<SubscriptionPlanProps> = ({
  visible,
  onClose,
  data,
}) => {
  const navigate = useNavigate();
  const { appContextdata } = useContext<any>(UserContext);

  const getLatestSubscriptionDetails = async (organizationId: number) => {
    try {
      const response : any = await axiosPost(getSubscriptionPlan, {
        OrganizationId: organizationId,
      });
      return response?.data?.ExtraData;
    } catch (err) {
      console.error("Error fetching subscription details:", err);
      return null;
    }
  };

  const handleEncrypt = async () => {
    let planDetails : SubscriptionData = data;
    if (appContextdata?.Organization) {
      const latest = await getLatestSubscriptionDetails(appContextdata?.Organization);
      if (latest) planDetails  = latest;
    }

    const encryptedPlanId = await encrypt(planDetails?.plan_id?.toString());
    const encryptedPlanDate = await encrypt(formatDateCommon(planDetails?.plan_end_date));

    return {
      id: encodeURIComponent(encryptedPlanId),
      date: encodeURIComponent(encryptedPlanDate)
    };
  };


  const handleNavigate = async () => {
    const { id, date } = await handleEncrypt();
    navigate(`/upgrade-plan/${id}/${date}`);
  };

  const handleNavigateTopUp =  () => {
    navigate(`/top-up-plan`);
  };

  return (
    <div
      className="position-fixed top-0 end-0 h-100 bg-white shadow"
      style={{
        width: "400px",
        maxWidth: "80vw",
        transform: visible ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.4s ease-in-out",
        zIndex: 1050,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center p-3 border-bottom"
        style={{ backgroundColor: "#F8A745" }}
      >
        <h5 className="mb-0 text-white">Subscription Plan</h5>
        <button
          className="btn btn-sm btn-light"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      <div className="p-4">
        <div className="row pb-2 mb-2 border-bottom">
          <div className="col-8 fw-bold">Plan Name:</div>
          <div className="col-4">{data?.PlanName || "N/A"}</div>
        </div>
        <div className="row pb-2 mb-2 border-bottom">
          <div className="col-8 fw-bold">Plan Start Date:</div>
          <div className="col-4">
            {formatDateCommon(data?.PlanStartDate) || "N/A"}
          </div>
        </div>
        <div className="row pb-2 mb-2 border-bottom">
          <div className="col-8 fw-bold">Plan End Date:</div>
          <div className="col-4">
            {formatDateCommon(data?.PlanEndDate) || "N/A"}
          </div>
        </div>
        <div className="row pb-2 mb-2 border-bottom">
          <div className="col-8 fw-bold">Subscription:</div>
          <div className="col-4">
            {data?.IsSubscriptionActive ? "Active" : "Inactive"}
          </div>
        </div>
        <div className="row pb-2 mb-2 border-bottom">
          <div className="col-8 fw-bold">Response Limit:</div>
          <div className="col-4">{data?.UserResponseCount}</div>
        </div>
        <div className="row pb-2 mb-2 border-bottom">
          <div className="col-8 fw-bold">Sentiment Analysis:</div>
          <div className="col-4">{data?.AllowSentiment ? "Yes" : "No"}</div>
        </div>
        <div className="row pb-2 mb-2 border-bottom">
          <div className="col-8 fw-bold">Public Survey Sharing:</div>
          <div className="col-4">{data?.AllowPublicSurvey ? "Yes" : "No"}</div>
        </div>
        <div className="row pb-2 mb-2 border-bottom">
          <div className="col-8 fw-bold">Demography Data Collection:</div>
          <div className="col-4">{data?.AllowDemography ? "Yes" : "No"}</div>
        </div>
        <div className="row pb-2 mb-2 border-bottom">
          <div className="col-8 fw-bold">Report Generation:</div>
          <div className="col-4">{data?.Report ? "Yes" : "No"}</div>
        </div>
        <div className="row pb-2 mb-2 border-bottom">
          <div className="col-8 fw-bold">Total TopUp Unit:</div>
          <div className="col-4">{data?.TotalTopUpUnit ?? 0}</div>
        </div>
        <div className="row pb-2 mb-2 border-bottom">
          <div className="col-8 fw-bold">Total Consumed Unit:</div>
          <div className="col-4">{data?.TotalConsumedUnit ?? 0}</div>
        </div>
        <div className="row pb-2 mb-2 border-bottom">
          <div className="col-8 fw-bold">TopUp Balance:</div>
          <div className="col-4">{data?.UnitBalance ?? 0}</div>
        </div>
      </div>

      <div className="ps-4">
        <Button
          onClick={handleNavigate}
          className="primary-btn btn-add"
          style={{marginRight : "10px", marginBottom: "10px"}}
        >
          Upgrade Plan
        </Button>
        <Button
          onClick={handleNavigateTopUp}
          className="primary-btn btn-add"
          style={{ marginRight: "10px", marginBottom: "10px"}}
        >
          Add Top-Up
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionPlan;
